import * as React from "react";
import { action, computed, observable, autorunAsync } from "mobx";
import { Promise } from 'es6-promise';
import { Strings } from "../Stores/StringsStore";
import * as DocumentReader from "../DocumentReader";
import { EPubFileSetting, EPubFileSettingsStore } from "./EPubFileSettingsStore";


class NavigationChapters {
	previous: DocumentReader.Chapter = null;
	current: DocumentReader.Chapter = null;
	next: DocumentReader.Chapter = null;
}

const zoomMin = -10;
const zoomFactor = 1.125;
const zoomMax = 10;

export class EPubStore {

	readonly id: string;
	readonly docPath: string;
	private autoSave: any;
	@observable rootChapters: DocumentReader.Chapter[] = null;
	@observable current?: string = null;
	@observable previous?: string = null;
	@observable next?: string = null;
	@observable zoom: number = 0;
	@observable scrollX: number = 0;
	@observable scrollY: number = 0;

	constructor(id: string) {
		this.id = id;
		this.docPath = encodeURI(`/files/${this.id}.epub`);
		this.autoSave = autorunAsync(`Automatically save e-pub settings for file ${this.id}`, () => {
			this.saveSettings();
		});
	}

	private saveSettings(): void {
		if (this.current) {
			let x: EPubFileSetting = {
				guid: this.id,
				zoom: this.zoom,
				target: this.current,
				scrollX: this.scrollX,
				scrollY: this.scrollY,
			}
			EPubFileSettingsStore.addSettings(x);
		}
	}

	private updateTarget(chapters: DocumentReader.Chapter[], target: string, navigation: NavigationChapters): boolean {
		let result = false;
		chapters.map(c => {
			if (target) {
				c.isCurrent = c.linkTarget === target;
			}
			if (c.isCurrent) {
				navigation.current = c;
				result = true;
			}
			else if (navigation.current) {
				if (!navigation.next) {
					navigation.next = c;
				}
			}
			else {
				navigation.previous = c;
			}
			let expand = this.updateTarget(c.subChapters, target, navigation);
			if (expand) {
				c.isExpanded = true;
				result = true;
			}
		});
		return result;
	}

	@action setScrollRelative(x: number, y: number) {
		this.scrollX = x;
		this.scrollY = y;
	}

	@action navigateTo(target: string): void {
		if (this.rootChapters) {
			this.scrollX = 0;
			this.scrollY = 0;
			let navigation: NavigationChapters = new NavigationChapters();
			this.updateTarget(this.rootChapters, target, navigation);
			if (navigation.current) {
				this.current = navigation.current.linkTarget;
				this.previous = navigation.previous ? navigation.previous.linkTarget : null;
			}
			else {
				this.current = null;
				this.previous = null;
			}
			this.next = navigation.next ? navigation.next.linkTarget : null;
		}
	}

	@computed get path(): string {
		if (this.current) {
			return this.docPath + "/" + encodeURI(this.current);
		}
		else {
			return null;
		}
	}

	@computed get disabledPrevious(): boolean {
		return (!this.previous);
	}

	@action navigatePrevious = () => {
		if (this.previous) {
			this.navigateTo(this.previous);
		}
	}

	@computed get disabledNext(): boolean {
		return (!this.next);
	}

	@action navigateNext = () => {
		if (this.next) {
			this.navigateTo(this.next);
		}
	}

	@computed get disabledZoomIn(): boolean {
		return (this.zoom >= zoomMax || !this.current);
	}

	@action zoomIn = () => {
		if (this.zoom < zoomMax) {
			this.zoom++;
		}
	}

	@computed get disabledZoomOut(): boolean {
		return (this.zoom <= zoomMin || !this.current);
	}

	@action zoomOut = () => {
		if (this.zoom > zoomMin) {
			this.zoom--;
		}
	}

	@computed get zoomValue(): string {
		let factor = Math.pow(zoomFactor, this.zoom);
		let value = (Math.round(100.0 * factor) / 100).toString();
		return value;
	}

}
